/**
 * The per-card General | Telegram | MAX override controller (design §7.2/§7.3), extended
 * *(delta 2026-09-23)* with the General tab's own provenance and pending-reset model (§17.1).
 * Built as a plain function rather than a hook — it derives everything from state the page already
 * owns (`useSettingsForm` per resource, the scope `useState`, the page's `pendingResets` set), so it
 * needs no lifecycle of its own; the page calls it once per card, on every render.
 */
import type { ChannelScope } from '../ui/components/ChannelScopeTabs';
import type { FieldSource } from '../ui/components/FieldSourceChip';
import { dropCardFields, dropField, stableEqual } from './formModel';
import { generalFieldProvenance } from './provenance';
import type {
  ChatbotChannel,
  ChatbotGeneralSettings,
  ChatbotSettingsFields,
  ChatbotSettingsOverrides,
  EffectiveChatbotSettings,
} from './types';

export interface ChannelForm {
  draft: ChatbotSettingsOverrides | null;
  setDraft: (value: ChatbotSettingsOverrides) => void;
  patch: (partial: ChatbotSettingsOverrides) => void;
}

export interface ChannelOverrideControllerInput {
  scope: ChannelScope;
  cardFields: (keyof ChatbotSettingsFields)[];
  generalDraft: ChatbotSettingsFields;
  /** The general row as last loaded from the server — the pending-reset model's baseline
   * (`loaded[f]`, `defaultedFields`, `platformDefaults`). Undefined only before the first load. */
  generalLoaded?: ChatbotGeneralSettings;
  /** Updates one field of the general row's draft — the GENERAL scope's `field(...).onChange`. */
  setGeneralField: <K extends keyof ChatbotSettingsFields>(
    key: K,
    value: ChatbotSettingsFields[K],
  ) => void;
  /** The general row's pending-reset set (design §17.1.4) — General scope only. */
  pendingResets?: ReadonlySet<keyof ChatbotSettingsFields>;
  /** "Use platform default" on one field — draft[f] = loaded[f]; the caller also updates
   * `pendingResets` per §17.1.4's two-branch rule (adds it unless the field was already defaulted). */
  onUseDefault?: (field: keyof ChatbotSettingsFields) => void;
  /** "Keep your value" — un-pends a field, restoring the display to the last-saved value. */
  onKeepYours?: (field: keyof ChatbotSettingsFields) => void;
  channelForms: Record<ChatbotChannel, ChannelForm>;
  /** The last-saved (or confirmed-absent) override per channel — used only to tell whether the
   * ACTIVE channel's edits touch this card ("Reset to general" needs a confirm only then). */
  channelBaselines: Record<ChatbotChannel, ChatbotSettingsOverrides>;
  effective: Record<ChatbotChannel, EffectiveChatbotSettings | undefined>;
  canManage: boolean;
  /** Fields locked by a platform-level capability switch (design §9.1) — disabled on every tab. */
  lockedFields?: Partial<Record<keyof ChatbotSettingsFields, boolean>>;
}

export interface FieldScope<K extends keyof ChatbotSettingsFields> {
  value: ChatbotSettingsFields[K];
  disabled: boolean;
  locked: boolean;
  source: FieldSource;
  /** General tab only: `source === 'DEFAULT'` because of an uncommitted "Use platform default" —
   * the dashed "Platform default · not saved yet" chip variant. */
  pending: boolean;
  /** General tab only: `source === 'GENERAL'` and the value happens to equal the platform default —
   * selects the "Same as the platform default" hint over "Platform default: {value}". */
  sameAsDefault: boolean;
  /** General tab only — the platform default for this field, for the caller to format into the hint
   * text with `formatSettingValue`. Undefined when `platformDefaults` has not loaded yet. */
  platformDefaultValue?: ChatbotSettingsFields[K];
  onChange: (value: ChatbotSettingsFields[K]) => void;
  /** Present only when this field is currently overridden on the active channel scope. */
  onUseGeneral?: () => void;
  /** Present only when inherited but the card already has ≥1 override elsewhere on this channel. */
  onCustomiseRow?: () => void;
  /** General tab only, when `source !== 'DEFAULT'` — "Use platform default". */
  onUseDefault?: () => void;
  /** General tab only, while `pending` — "Keep your value" (undoes the pending reset). */
  onKeepYours?: () => void;
}

export interface ChannelOverrideController {
  /** Whether the given channel has ≥1 overridden field belonging to this card — the tab dot badge. */
  hasOverride: (channel: ChatbotChannel) => boolean;
  /** Whether the ACTIVE scope (when it is a channel) has ≥1 overridden field on this card. */
  activeChannelHasOverride: boolean;
  /** Whether the ACTIVE channel's card fields differ from what is actually saved — "Reset to
   * general" only needs to confirm when this is true (design §7.3). */
  activeChannelCardDirty: boolean;
  field: <K extends keyof ChatbotSettingsFields>(key: K) => FieldScope<K>;
  /** "Customise for <channel>" — seeds every card field into the draft from the effective view. */
  customiseCard: () => void;
  /** "Reset to general" — drops every card field from the draft (channel-scoped, not the other card's). */
  resetCard: () => void;
  /** General tab only: fields eligible for the card's "Use platform defaults…" action — set in the
   * loaded row or edited in the draft, excluding locked fields (design §17.1.5). */
  resettableGeneralFields: (keyof ChatbotSettingsFields)[];
  /** General tab only — applies "Use platform default" to every {@link resettableGeneralFields}. */
  useCardDefaults: () => void;
}

function isOverridden(form: ChannelForm, field: keyof ChatbotSettingsFields): boolean {
  return Boolean(form.draft && Object.prototype.hasOwnProperty.call(form.draft, field));
}

export function createChannelOverrideController(
  input: ChannelOverrideControllerInput,
): ChannelOverrideController {
  const {
    scope,
    cardFields,
    generalDraft,
    generalLoaded,
    setGeneralField,
    pendingResets,
    onUseDefault,
    onKeepYours,
    channelForms,
    channelBaselines,
    effective,
    canManage,
    lockedFields,
  } = input;

  const hasOverride = (channel: ChatbotChannel) =>
    cardFields.some((field) => isOverridden(channelForms[channel], field));

  const activeChannel: ChatbotChannel | null = scope === 'GENERAL' ? null : scope;
  const activeForm = activeChannel ? channelForms[activeChannel] : null;
  const activeChannelHasOverride = activeChannel ? hasOverride(activeChannel) : false;

  function field<K extends keyof ChatbotSettingsFields>(key: K): FieldScope<K> {
    const locked = lockedFields?.[key] ?? false;

    if (!activeChannel || !activeForm) {
      const loadedValue = generalLoaded?.[key];
      const platformDefaultValue = generalLoaded?.platformDefaults?.[key];
      const pending = pendingResets?.has(key) ?? false;
      const displayValue =
        pending && platformDefaultValue !== undefined ? platformDefaultValue : generalDraft[key];
      const wasDefaulted = generalLoaded?.defaultedFields?.includes(key as string) ?? false;
      const isDirty = generalLoaded ? !stableEqual(generalDraft[key], loadedValue) : false;
      const isEqualToDefault =
        platformDefaultValue !== undefined && stableEqual(displayValue, platformDefaultValue);
      const provenance = generalFieldProvenance(wasDefaulted, isDirty, isEqualToDefault, pending);
      const source: FieldSource = provenance.kind.startsWith('DEFAULT') ? 'DEFAULT' : 'GENERAL';

      return {
        value: displayValue,
        disabled: !canManage || locked,
        locked,
        source,
        pending: provenance.kind === 'DEFAULT_PENDING',
        sameAsDefault: provenance.kind === 'SAME_AS_DEFAULT',
        platformDefaultValue,
        onChange: (next) => setGeneralField(key, next),
        // Row actions need `canManage` too (design §17.1.9): without it, the chips and hints stay
        // informational but the row (and card) actions must not render at all.
        onUseDefault:
          canManage &&
          !locked &&
          provenance.kind !== 'DEFAULT' &&
          provenance.kind !== 'DEFAULT_PENDING'
            ? () => onUseDefault?.(key)
            : undefined,
        onKeepYours:
          canManage && !locked && provenance.kind === 'DEFAULT_PENDING'
            ? () => onKeepYours?.(key)
            : undefined,
      };
    }

    const overridden = isOverridden(activeForm, key);
    if (overridden) {
      const value = (activeForm.draft as ChatbotSettingsOverrides)[key] as ChatbotSettingsFields[K];
      return {
        value,
        disabled: !canManage || locked,
        locked,
        source: 'OVERRIDE',
        pending: false,
        sameAsDefault: false,
        onChange: (next) => activeForm.patch({ [key]: next } as ChatbotSettingsOverrides),
        onUseGeneral: () => activeForm.setDraft(dropField(activeForm.draft ?? {}, key)),
      };
    }

    const effectiveView = effective[activeChannel];
    const effectiveValue = effectiveView?.[key] ?? generalDraft[key];
    const inheritedSource: FieldSource = effectiveView?.defaultedFields?.includes(key as string)
      ? 'DEFAULT'
      : 'GENERAL';
    return {
      value: effectiveValue,
      disabled: true,
      locked,
      source: inheritedSource,
      pending: false,
      sameAsDefault: false,
      onChange: () => undefined,
      onCustomiseRow: activeChannelHasOverride
        ? () => activeForm.patch({ [key]: effectiveValue } as ChatbotSettingsOverrides)
        : undefined,
    };
  }

  const activeChannelCardDirty = (() => {
    if (!activeChannel || !activeForm) return false;
    const baseline = channelBaselines[activeChannel] ?? {};
    const draft = activeForm.draft ?? {};
    return cardFields.some((f) => !stableEqual(draft[f], baseline[f]));
  })();

  const resettableGeneralFields = activeChannel
    ? []
    : cardFields.filter((f) => {
        if (lockedFields?.[f]) return false;
        const wasSet = generalLoaded
          ? !(generalLoaded.defaultedFields?.includes(f as string) ?? false)
          : false;
        const isDirty = generalLoaded ? !stableEqual(generalDraft[f], generalLoaded[f]) : false;
        return wasSet || isDirty;
      });

  return {
    hasOverride,
    activeChannelHasOverride,
    activeChannelCardDirty,
    field,
    customiseCard: () => {
      if (!activeChannel || !activeForm) return;
      const patch: ChatbotSettingsOverrides = {};
      for (const key of cardFields) {
        const value = effective[activeChannel]?.[key] ?? generalDraft[key];
        (patch as Record<string, unknown>)[key] = value;
      }
      activeForm.patch(patch);
    },
    resetCard: () => {
      if (!activeChannel || !activeForm) return;
      activeForm.setDraft(dropCardFields(activeForm.draft ?? {}, cardFields));
    },
    resettableGeneralFields,
    useCardDefaults: () => {
      if (activeChannel) return;
      for (const key of resettableGeneralFields) {
        onUseDefault?.(key);
      }
    },
  };
}
